// useUser() — trades the current Firebase ID token for a HubUser via /api/me.
//
// Split from useAuth() so pages that only need "am I signed in" avoid the
// /api/me round-trip. AuthGate uses this to enforce the "must be a Squint
// team member" boundary; everything else should read from useEffectiveRole
// so persona-based capping applies.
//
// Only the raw auth signals live here (role + isViewer). Persona-fused
// booleans (isEditor / isPmm / isSales / canEditContent) live on
// useEffectiveRole so a naive import can't accidentally bypass persona
// gating.
import { useQuery } from "@tanstack/react-query";
import type { HubUser, HubUserRole } from "@release-hub/shared";
import { api } from "@/api/client";
import { useAuth } from "./useAuth";

export interface HubUserResult {
  user: HubUser | null;
  /** Raw auth role from /api/me. Consumers gating UI should almost
   *  always read isEditor/isPmm/... from useEffectiveRole instead — that
   *  hook applies persona-based capping. This is the auth ground truth. */
  role: HubUserRole;
  /** True iff the user is a recognized Squint team member (any tier —
   *  editor / pmm / viewer / sales). The AuthGate boundary. */
  isViewer: boolean;
  isLoading: boolean;
  isSignedIn: boolean;
  error: unknown;
}

export function useUser(): HubUserResult {
  const auth = useAuth();
  const query = useQuery({
    queryKey: ["me"],
    queryFn: () => api<HubUser>("/api/me"),
    enabled: !!auth.user,
    // /api/me is cheap and rarely changes; 5min is plenty.
    staleTime: 5 * 60_000,
    retry: false,
  });
  const role: HubUserRole = query.data?.role ?? "none";
  return {
    user: query.data ?? null,
    role,
    isViewer: query.data?.isSquintTeamMember ?? false,
    isLoading: !auth.ready || (!!auth.user && query.isPending),
    isSignedIn: !!auth.user,
    error: query.error,
  };
}
